import { useVerificationsQuery } from "Api/Queries/Verifications/useVerificationsQuery";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import {
  BankVerificationStatus,
  IdentityVerificationStatus,
  RiskProfileStatus,
  UserStatus,
  type UserVerificationDetailDto,
  Version,
} from "Infrastructure/Api/Api";
import { AccessRoleCodes } from "Models/AccessRoleCodes";

// Mirror of CustomerTierRules.HasAcceptedIdentityVerification on the backend: a pending manual
// review qualifies, a verified identity only with a risk profile short of Unacceptable.
const hasAcceptedIdentity = (verification: UserVerificationDetailDto) =>
  verification.identityVerificationStatus ===
    IdentityVerificationStatus.ToVerify ||
  (verification.identityVerificationStatus ===
    IdentityVerificationStatus.Verified &&
    verification.riskProfile !== RiskProfileStatus.Unacceptable);

export const useIsUserInMooneLite = () => {
  const user = useLoggedInUser();
  const { data: verification } = useVerificationsQuery(user?.userID);

  // Narrow: user's tier is Lite. Stable life-cycle attribute.
  const isLiteTier = user?.version === Version.MooneLite;

  // Doc-expiration is a runtime state — was Verified, primary ID expired since.
  const isDocExpired =
    verification?.identityVerificationStatus ===
    IdentityVerificationStatus.Expired;

  // Full Mo.one needs identity KYC and a verified bank account. When the identity part is done,
  // the only way out of Lite is connecting a bank, so CTAs must point there, not at the wizard.
  // Active is the evidence the wizard went through: the backend sets it on the final step and
  // nowhere earlier. An identity at ToVerify alone is not enough — a failed document scan parks it
  // there on the first step, and that user must still be sent back to finish the wizard.
  // Customer context only: other grants (Employee, Hunter) inherit the person's Lite version but
  // the bank connect is a Customer flow, and the route rejects roles like Employee outright.
  const isLiteBecauseOfBank =
    isLiteTier &&
    user?.accessRoleCode === AccessRoleCodes.Customer &&
    user?.userStatus === UserStatus.Active &&
    verification !== undefined &&
    hasAcceptedIdentity(verification) &&
    verification.bankVerificationStatus !== BankVerificationStatus.Verified;

  // Combined "account is restricted" — covers both reasons UI should treat as limited.
  const isUserInMooneLite = isLiteTier || isDocExpired;

  const isUserInBlockedMoonLite =
    isUserInMooneLite && user?.userStatus === UserStatus.BlockedFinances;

  return {
    isUserInMooneLite,
    isUserInBlockedMoonLite,
    isLiteTier,
    isLiteBecauseOfBank,
    isDocExpired,
  };
};
